import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Users, Shield, UserCheck, UserX, Edit2, 
  RefreshCw, Search, Check, X, AlertTriangle,
  Plus, UserPlus, Trash2, Clock, Mail, Upload, Copy, KeyRound, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const roleLabels = {
  ADMIN: { label: 'Administrador', color: 'bg-purple-100 text-purple-700', icon: Shield },
  ADMINISTRATIVO: { label: 'Administrativo', color: 'bg-blue-100 text-blue-700', icon: Users },
  ENFERMERA: { label: 'Enfermera', color: 'bg-emerald-100 text-emerald-700', icon: UserCheck },
  CAJA: { label: 'Caja', color: 'bg-amber-100 text-amber-700', icon: Users }
};

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [deactivateUser, setDeactivateUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'ENFERMERA', rut: '', phone: '', box_area: '' });
  const [bulkText, setBulkText] = useState('');
  const [bulkRole, setBulkRole] = useState('ENFERMERA');
  const [inviting, setInviting] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [credentialsToShow, setCredentialsToShow] = useState(null); // {email, password, name, emailSent}

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/roles`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permiso para ver esta página');
      } else {
        toast.error('Error al cargar los usuarios');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInviteUser = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Email y nombre son requeridos');
      return;
    }
    
    setInviting(true);
    try {
      const res = await axios.post(`${API}/users/invite`, inviteForm, {
        withCredentials: true
      });
      if (res.data?.email_sent) {
        toast.success('Usuario invitado. Correo con credenciales enviado.');
      } else {
        toast.success('Usuario invitado.');
        if (res.data?.email_error) {
          toast.warning(`No se pudo enviar el correo. Comparte la contraseña manualmente.`);
        }
      }
      // Show generated password to admin for manual sharing
      setCredentialsToShow({
        email: res.data?.user?.email || inviteForm.email,
        password: res.data?.password || '',
        name: res.data?.user?.name || inviteForm.name,
        emailSent: !!res.data?.email_sent,
      });
      setShowInviteModal(false);
      setInviteForm({ email: '', name: '', role: 'ENFERMERA', rut: '', phone: '', box_area: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al invitar usuario');
    } finally {
      setInviting(false);
    }
  };

  const handleBulkInvite = async () => {
    if (!bulkText.trim()) {
      toast.error('Ingrese al menos un usuario');
      return;
    }
    
    // Parse bulk text: each line should be "email, nombre" or "email"
    const lines = bulkText.split('\n').filter(l => l.trim());
    const users = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      return {
        email: parts[0],
        name: parts[1] || parts[0].split('@')[0],
        role: bulkRole
      };
    });
    
    if (users.length === 0) {
      toast.error('No se encontraron usuarios válidos');
      return;
    }
    
    setInviting(true);
    try {
      const response = await axios.post(`${API}/users/invite-bulk`, users, {
        withCredentials: true
      });
      toast.success(response.data.message);
      setShowBulkModal(false);
      setBulkText('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al invitar usuarios');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await axios.put(`${API}/users/${userId}`, data, {
        withCredentials: true
      });
      toast.success('Usuario actualizado');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDeactivateUser = async () => {
    if (!deactivateUser) return;
    
    try {
      await axios.delete(`${API}/users/${deactivateUser.user_id}`, {
        withCredentials: true
      });
      toast.success('Usuario desactivado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desactivar');
    } finally {
      setDeactivateUser(null);
    }
  };

  const handleDeletePermanent = async () => {
    if (!deleteUser) return;
    
    try {
      await axios.delete(`${API}/users/${deleteUser.user_id}/permanent`, {
        withCredentials: true
      });
      toast.success('Usuario eliminado permanentemente');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleteUser(null);
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}`, { active: true }, {
        withCredentials: true
      });
      toast.success('Usuario activado');
      fetchData();
    } catch (error) {
      toast.error('Error al activar usuario');
    }
  };

  const handleResendInvitation = async (u) => {
    setResendingId(u.user_id);
    try {
      const res = await axios.post(`${API}/users/${u.user_id}/resend-invitation`, {}, {
        withCredentials: true
      });
      if (res.data?.password) {
        setCredentialsToShow({
          email: u.email,
          name: u.name,
          password: res.data.password,
          emailSent: !!res.data.email_sent,
        });
      }
      toast.success(res.data?.email_sent ? `Invitación reenviada a ${u.email}` : 'Contraseña regenerada. Compártela manualmente.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reenviar invitación');
    } finally {
      setResendingId(null);
    }
  };

  const handleApproveUser = async (u) => {
    setApprovingId(u.user_id);
    try {
      await axios.post(`${API}/users/${u.user_id}/approve`, {}, { withCredentials: true });
      toast.success(`${u.name} aprobado. Ya puede usar el sistema.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar usuario');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRegeneratePassword = async (u) => {
    setRegeneratingId(u.user_id);
    try {
      const res = await axios.post(`${API}/users/${u.user_id}/regenerate-password`, {}, {
        withCredentials: true
      });
      if (res.data?.password) {
        setCredentialsToShow({
          email: u.email,
          name: u.name,
          password: res.data.password,
          emailSent: !!res.data.email_sent,
        });
      }
      toast.success('Contraseña regenerada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al regenerar contraseña');
    } finally {
      setRegeneratingId(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeUsers = filteredUsers.filter(u => u.active !== false && u.status !== 'PENDIENTE' && u.status !== 'PENDIENTE_APROBACION');
  const pendingUsers = filteredUsers.filter(u => u.status === 'PENDIENTE' && u.active !== false);
  const pendingApprovalUsers = filteredUsers.filter(u => u.status === 'PENDIENTE_APROBACION' && u.active !== false);
  const inactiveUsers = filteredUsers.filter(u => u.active === false);

  // Check if current user is admin
  if (currentUser?.role !== 'ADMIN') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Card className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Acceso Restringido</h2>
            <p className="text-slate-500">Solo los administradores pueden acceder a esta sección.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="users-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Administración de Usuarios</h1>
            <p className="text-slate-500 mt-1">Gestiona los usuarios y sus roles</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchData}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowBulkModal(true)}
              className="gap-2"
              data-testid="bulk-invite-btn"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Importar</span>
            </Button>
            <Button 
              onClick={() => setShowInviteModal(true)}
              className="btn-accent gap-2"
              data-testid="invite-user-btn"
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Invitar Usuario</span>
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 input-clinical"
                  data-testid="search-users"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{activeUsers.length}</p>
                <p className="text-xs text-slate-500">Activos</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{pendingUsers.length}</p>
                <p className="text-xs text-slate-500">Pendientes</p>
              </div>
            </div>
          </Card>
          {Object.entries(roleLabels).slice(0, 3).map(([role, info]) => {
            const count = activeUsers.filter(u => u.role === role).length;
            const Icon = info.icon;
            return (
              <Card key={role} className="p-4 hidden md:block">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${info.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-xs text-slate-500">{info.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue={pendingApprovalUsers.length > 0 ? "approval" : "active"} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="active" className="gap-2">
              <UserCheck className="w-4 h-4" />
              Activos ({activeUsers.length})
            </TabsTrigger>
            <TabsTrigger value="approval" className="gap-2 data-[state=active]:bg-white">
              <CheckCircle2 className="w-4 h-4" />
              Por aprobar
              {pendingApprovalUsers.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] font-bold">
                  {pendingApprovalUsers.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" className="gap-2">
              <Clock className="w-4 h-4" />
              Invitados ({pendingUsers.length})
            </TabsTrigger>
            <TabsTrigger value="inactive" className="gap-2">
              <UserX className="w-4 h-4" />
              Inactivos ({inactiveUsers.length})
            </TabsTrigger>
          </TabsList>

          {/* Active Users */}
          <TabsContent value="active">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="loading-spinner w-8 h-8"></div>
              </div>
            ) : activeUsers.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No hay usuarios activos</p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {activeUsers.map((u) => (
                  <Card key={u.user_id} className="hover:shadow-md transition-shadow" data-testid={`user-card-${u.user_id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={u.picture} alt={u.name} />
                            <AvatarFallback className="bg-blue-100 text-blue-600">
                              {getInitials(u.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-slate-900">{u.name}</h3>
                              {u.user_id === currentUser?.user_id && (
                                <Badge variant="outline" className="text-xs">Tú</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 flex-wrap">
                          {editingUser === u.user_id ? (
                            <div className="flex items-center gap-2">
                              <Select 
                                defaultValue={u.role}
                                onValueChange={(value) => handleUpdateUser(u.user_id, { role: value })}
                              >
                                <SelectTrigger className="w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(roleLabels).map(([role, info]) => (
                                    <SelectItem key={role} value={role}>
                                      {info.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingUser(null)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <Badge className={roleLabels[u.role]?.color || 'bg-slate-100'}>
                                {roleLabels[u.role]?.label || u.role}
                              </Badge>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRegeneratePassword(u)}
                                  disabled={regeneratingId === u.user_id || u.user_id === currentUser?.user_id}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  title="Regenerar contraseña"
                                  data-testid={`regenerate-pw-${u.user_id}`}
                                >
                                  <KeyRound className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingUser(u.user_id)}
                                  disabled={u.user_id === currentUser?.user_id}
                                  data-testid={`edit-user-${u.user_id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setDeactivateUser(u)}
                                  disabled={u.user_id === currentUser?.user_id}
                                  data-testid={`deactivate-user-${u.user_id}`}
                                >
                                  <UserX className="w-4 h-4" />
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Pending Users */}
          <TabsContent value="pending">
            {pendingUsers.length === 0 ? (
              <Card className="p-8 text-center">
                <Clock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500 mb-4">No hay usuarios pendientes de activación</p>
                <Button onClick={() => setShowInviteModal(true)} className="btn-accent gap-2">
                  <UserPlus className="w-4 h-4" />
                  Invitar Usuario
                </Button>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pendingUsers.map((u) => (
                  <Card key={u.user_id} className="border-l-4 border-l-amber-400">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                            <Mail className="w-6 h-6 text-amber-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{u.name}</h3>
                            <p className="text-sm text-slate-500">{u.email}</p>
                            <p className="text-xs text-amber-600 mt-1">
                              Esperando primer inicio de sesión
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <Badge className={roleLabels[u.role]?.color || 'bg-slate-100'}>
                            {roleLabels[u.role]?.label || u.role}
                          </Badge>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResendInvitation(u)}
                              disabled={resendingId === u.user_id}
                              className="gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                              data-testid={`resend-invite-${u.user_id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                              {resendingId === u.user_id ? 'Enviando...' : 'Regenerar clave'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingUser(u.user_id)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteUser(u)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Pending Approval Users (second verification) */}
          <TabsContent value="approval">
            {pendingApprovalUsers.length === 0 ? (
              <Card className="p-8 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">
                  No hay usuarios esperando aprobación
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  Aquí aparecerán los usuarios que ya validaron su primer ingreso con su contraseña.
                </p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pendingApprovalUsers.map((u) => (
                  <Card key={u.user_id} className="border-l-4 border-l-emerald-500 shadow-sm">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900" data-testid={`approval-user-name-${u.user_id}`}>{u.name}</h3>
                            <p className="text-sm text-slate-500">{u.email}</p>
                            <p className="text-xs text-emerald-700 mt-1 font-medium">
                              ✓ Primer ingreso validado — Esperando tu aprobación
                            </p>
                            {u.first_login_at && (
                              <p className="text-[10px] text-slate-400 mt-0.5">
                                Ingresó: {new Date(u.first_login_at).toLocaleString('es-CL')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Badge className={roleLabels[u.role]?.color || 'bg-slate-100'}>
                            {roleLabels[u.role]?.label || u.role}
                          </Badge>
                          <Button
                            onClick={() => handleApproveUser(u)}
                            disabled={approvingId === u.user_id}
                            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`approve-user-${u.user_id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            {approvingId === u.user_id ? 'Aprobando...' : 'Aprobar'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeactivateUser(u)}
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Inactive Users */}
          <TabsContent value="inactive">
            {inactiveUsers.length === 0 ? (
              <Card className="p-8 text-center">
                <UserX className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">No hay usuarios inactivos</p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {inactiveUsers.map((u) => (
                  <Card key={u.user_id} className="bg-slate-50 opacity-75">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-12 w-12 grayscale">
                            <AvatarImage src={u.picture} alt={u.name} />
                            <AvatarFallback className="bg-slate-200 text-slate-500">
                              {getInitials(u.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold text-slate-600">{u.name}</h3>
                            <p className="text-sm text-slate-400">{u.email}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleActivateUser(u.user_id)}
                          className="gap-2"
                        >
                          <UserCheck className="w-4 h-4" />
                          Activar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Invite User Modal */}
        <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Dar de alta usuario</DialogTitle>
              <DialogDescription>
                Ingresa los datos del nuevo colaborador. Recibirá un correo de bienvenida con un código de primer ingreso y podrá entrar con Google o con código por correo.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div className="sm:col-span-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  className="mt-1"
                  data-testid="invite-email"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="name">Nombre completo *</Label>
                <Input
                  id="name"
                  placeholder="Nombre y apellidos"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                  className="mt-1"
                  data-testid="invite-name"
                />
              </div>
              <div>
                <Label htmlFor="rut">RUT</Label>
                <Input
                  id="rut"
                  placeholder="12.345.678-9"
                  value={inviteForm.rut}
                  onChange={(e) => setInviteForm({...inviteForm, rut: e.target.value})}
                  className="mt-1"
                  data-testid="invite-rut"
                />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  placeholder="+56 9 1234 5678"
                  value={inviteForm.phone}
                  onChange={(e) => setInviteForm({...inviteForm, phone: e.target.value})}
                  className="mt-1"
                  data-testid="invite-phone"
                />
              </div>
              <div>
                <Label htmlFor="role">Rol</Label>
                <Select 
                  value={inviteForm.role} 
                  onValueChange={(value) => setInviteForm({...inviteForm, role: value})}
                >
                  <SelectTrigger className="mt-1" data-testid="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels).map(([role, info]) => (
                      <SelectItem key={role} value={role}>
                        {info.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="box_area">Box / Área</Label>
                <Input
                  id="box_area"
                  placeholder="Box 1, Recepción, etc."
                  value={inviteForm.box_area}
                  onChange={(e) => setInviteForm({...inviteForm, box_area: e.target.value})}
                  className="mt-1"
                  data-testid="invite-box-area"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleInviteUser} 
                disabled={inviting}
                className="btn-accent"
                data-testid="confirm-invite-btn"
              >
                {inviting ? 'Invitando...' : 'Invitar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Invite Modal */}
        <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Importar Usuarios</DialogTitle>
              <DialogDescription>
                Ingresa un usuario por línea en formato: <code className="bg-slate-100 px-1 rounded">email, nombre</code>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="bulk">Lista de usuarios</Label>
                <Textarea
                  id="bulk"
                  placeholder="ejemplo@correo.com, Juan Pérez&#10;otro@correo.com, María García&#10;tercero@correo.com"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  className="mt-1 min-h-[150px] font-mono text-sm"
                  data-testid="bulk-textarea"
                />
              </div>
              <div>
                <Label htmlFor="bulkRole">Rol para todos</Label>
                <Select 
                  value={bulkRole} 
                  onValueChange={setBulkRole}
                >
                  <SelectTrigger className="mt-1" data-testid="bulk-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels).map(([role, info]) => (
                      <SelectItem key={role} value={role}>
                        {info.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">
                Se procesarán {bulkText.split('\n').filter(l => l.trim()).length} usuarios
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleBulkInvite} 
                disabled={inviting}
                className="btn-accent"
                data-testid="confirm-bulk-btn"
              >
                {inviting ? 'Procesando...' : 'Importar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deactivate Dialog */}
        <AlertDialog open={!!deactivateUser} onOpenChange={() => setDeactivateUser(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Desactivar usuario?</AlertDialogTitle>
              <AlertDialogDescription>
                El usuario "{deactivateUser?.name}" no podrá acceder al sistema. 
                Podrás reactivarlo en cualquier momento.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeactivateUser}
                className="bg-red-600 hover:bg-red-700"
              >
                Desactivar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Permanent Dialog */}
        <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar usuario permanentemente?</AlertDialogTitle>
              <AlertDialogDescription>
                El usuario "{deleteUser?.name}" será eliminado permanentemente.
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeletePermanent}
                className="bg-red-600 hover:bg-red-700"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Credentials Reveal Modal (shown after invite / regenerate) */}
        <Dialog open={!!credentialsToShow} onOpenChange={(o) => !o && setCredentialsToShow(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-blue-600" />
                Credenciales generadas
              </DialogTitle>
              <DialogDescription>
                {credentialsToShow?.emailSent
                  ? 'Se envió un correo con estas credenciales. También puedes copiarlas y compartirlas manualmente.'
                  : 'No se pudo enviar el correo automáticamente. Copia estas credenciales y compártelas por otro canal (WhatsApp, mensaje, en persona).'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {credentialsToShow?.name && (
                <div className="text-sm text-slate-700">
                  Para <strong>{credentialsToShow.name}</strong>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Correo</Label>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <code className="flex-1 text-sm text-slate-900 break-all" data-testid="credentials-email">
                    {credentialsToShow?.email}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(credentialsToShow?.email || '')}
                    className="text-slate-500 hover:text-slate-900"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-slate-500">Contraseña</Label>
                <div className="flex items-center gap-2 bg-slate-900 rounded-lg px-4 py-3">
                  <code
                    className="flex-1 text-xl text-white font-mono tracking-wider"
                    data-testid="credentials-password"
                  >
                    {credentialsToShow?.password}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(credentialsToShow?.password || '')}
                    className="text-slate-300 hover:text-white hover:bg-slate-800"
                    data-testid="copy-password-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="bg-amber-50 border-l-4 border-amber-400 rounded p-3 text-xs text-amber-900 leading-relaxed">
                <strong>Importante:</strong> Esta contraseña se muestra <strong>solo una vez</strong>. Guárdala o cópiala ahora. Si el usuario la pierde, deberás regenerarla desde el listado.
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => copyToClipboard(
                  `Vacu-Track — Tus credenciales\nCorreo: ${credentialsToShow?.email}\nContraseña: ${credentialsToShow?.password}\n\nIngresa en el sistema con estos datos.`
                )}
                className="gap-2"
                data-testid="copy-full-credentials-btn"
              >
                <Copy className="w-4 h-4" />
                Copiar todo
              </Button>
              <Button onClick={() => setCredentialsToShow(null)} data-testid="close-credentials-btn">
                Listo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UsersPage;
